import { afterEach, describe, expect, it } from '@jest/globals';
import type { PackageJson } from '@ms-cloudpack/common-types';
import { createTestFileStructure, removeTempDir, type TestFileStructure } from '@ms-cloudpack/test-utilities';
import path from 'path';
import { resolveParentConfig } from './resolveParentConfig.js';

describe('resolveParentConfig', () => {
  let testPath: string | undefined;

  function createLinkedMonorepo(files: TestFileStructure) {
    const links: Record<string, string> = {};
    for (const [file, contents] of Object.entries(files)) {
      if (file.endsWith('/package.json') && !file.includes('node_modules')) {
        const name = (contents as PackageJson).name;
        links[`node_modules/${name}`] = path.dirname(file);
      }
    }

    return createTestFileStructure(files, { links });
  }

  afterEach(async () => {
    testPath && (await removeTempDir(testPath));
    testPath = undefined;
  });

  it('resolves a relative path in same directory', async () => {
    testPath = await createTestFileStructure({
      'cloudpack.base.json': {},
    });

    const resolvedPath = resolveParentConfig({
      configPath: path.join(testPath, 'cloudpack.config.json'),
      importSpecifier: './cloudpack.base.json',
    });

    expect(resolvedPath).toEqual(path.join(testPath, 'cloudpack.base.json'));
  });

  it('resolves relative JS config paths with all extensions', async () => {
    testPath = await createTestFileStructure({
      'foo.js': '',
      'bar.cjs': '',
      'baz.mjs': '',
    });

    const configPath = path.join(testPath, 'cloudpack.config.json');
    const cjsPath = resolveParentConfig({ importSpecifier: './foo.js', configPath });
    expect(cjsPath).toEqual(path.join(testPath, 'foo.js'));
    const jsPath = resolveParentConfig({ importSpecifier: './bar.cjs', configPath });
    expect(jsPath).toEqual(path.join(testPath, 'bar.cjs'));
    const mjsPath = resolveParentConfig({ importSpecifier: './baz.mjs', configPath });
    expect(mjsPath).toEqual(path.join(testPath, 'baz.mjs'));
  });

  it('errors on relative JS config path without extension', async () => {
    testPath = await createTestFileStructure({
      'config.js': '',
    });
    const configPath = path.join(testPath, 'cloudpack.config.json');

    expect(resolveParentConfig({ configPath, importSpecifier: './config.js' })).toBeTruthy();
    expect(() => resolveParentConfig({ configPath, importSpecifier: './config' })).toThrow(
      `Could not resolve "extends": "./config"`,
    );
  });

  it('resolves a relative path to parent directory', async () => {
    testPath = await createLinkedMonorepo({
      'cloudpack.base.json': {},
    });

    const resolvedPath = resolveParentConfig({
      configPath: path.join(testPath, 'apps/app/cloudpack.config.json'),
      importSpecifier: '../../cloudpack.base.json',
    });

    expect(resolvedPath).toEqual(path.join(testPath, 'cloudpack.base.json'));
  });

  it('resolves a package import specifier with a path (no exports map)', async () => {
    // this one needs a more realistic/complete structure for import resolution
    testPath = await createLinkedMonorepo({
      'apps/app/package.json': { name: 'app', version: '1.0.0', devDependencies: { scripts: '*' } },
      'scripts/package.json': { name: 'scripts', version: '1.0.0', main: 'index.js' },
      'scripts/cloudpack.base.json': {},
    });

    const resolvedPath = resolveParentConfig({
      configPath: path.join(testPath, 'apps/app/cloudpack.config.json'),
      // This specifier can be resolved because scripts/package.json doesn't have an exports map
      importSpecifier: 'scripts/cloudpack.base.json',
    });

    expect(resolvedPath).toEqual(path.join(testPath, 'scripts/cloudpack.base.json'));
  });

  it('resolves a package import specifier with a path to a JS file (no exports map)', async () => {
    testPath = await createLinkedMonorepo({
      'apps/app/package.json': { name: 'app', version: '1.0.0', devDependencies: { scripts: '*' } },
      'scripts/package.json': { name: 'scripts', version: '1.0.0', main: 'index.js' },
      'scripts/cloudpackBase.js': '',
    });

    const resolvedPath = resolveParentConfig({
      configPath: path.join(testPath, 'apps/app/cloudpack.config.json'),
      // This specifier can be resolved because scripts/package.json doesn't have an exports map
      importSpecifier: 'scripts/cloudpackBase.js',
    });

    expect(resolvedPath).toEqual(path.join(testPath, 'scripts/cloudpackBase.js'));
  });

  it('resolves a scoped package import specifier with a path', async () => {
    testPath = await createLinkedMonorepo({
      'apps/app/package.json': { name: 'app', version: '1.0.0', devDependencies: { '@scope/foo': '*' } },
      'packages/foo/package.json': { name: '@scope/foo', version: '1.0.0', main: 'index.js' },
      'packages/foo/cloudpack.foo.json': {},
    });

    const resolvedPath = resolveParentConfig({
      configPath: path.join(testPath, 'apps/app/cloudpack.config.json'),
      importSpecifier: '@scope/foo/cloudpack.foo.json',
    });

    expect(resolvedPath).toEqual(path.join(testPath, 'packages/foo/cloudpack.foo.json'));
  });

  it('resolves a package import specifier (no path) if the cloudpack config is main', async () => {
    testPath = await createLinkedMonorepo({
      'apps/app/package.json': { name: 'app', version: '1.0.0', devDependencies: { 'cloudpack-base': '*' } },
      'node_modules/cloudpack-base/package.json': {
        name: 'cloudpack-base',
        version: '1.0.0',
        main: 'cloudpack.base.json',
      },
      'node_modules/cloudpack-base/cloudpack.base.json': {},
    });

    const resolvedPath = resolveParentConfig({
      configPath: path.join(testPath, 'apps/app/cloudpack.config.json'),
      importSpecifier: 'cloudpack-base',
    });

    expect(resolvedPath).toEqual(path.join(testPath, 'node_modules/cloudpack-base/cloudpack.base.json'));
  });

  // This seems to be a limitation of import-meta-resolve by default
  it('throws on a package import specifier (no path) if the cloudpack config is module', async () => {
    testPath = await createLinkedMonorepo({
      'apps/app/package.json': { name: 'app', version: '1.0.0', devDependencies: { 'cloudpack-base': '*' } },
      'node_modules/cloudpack-base/package.json': {
        name: 'cloudpack-base',
        version: '1.0.0',
        module: 'cloudpack.base.json',
      },
      'node_modules/cloudpack-base/cloudpack.base.json': {},
    });

    const configPath = path.join(testPath, 'apps/app/cloudpack.config.json');
    const importSpecifier = 'cloudpack-base';

    expect(() => resolveParentConfig({ configPath, importSpecifier })).toThrow(
      `Could not resolve "extends": "${importSpecifier}":\nCannot find package`,
    );
  });

  it('resolves a package import specifier (no path) if cloudpack config is . in exports map', async () => {
    testPath = await createLinkedMonorepo({
      'apps/app/package.json': { name: 'app', version: '1.0.0', devDependencies: { 'cloudpack-base': '*' } },
      'node_modules/cloudpack-base/package.json': {
        name: 'cloudpack-base',
        version: '1.0.0',
        exports: { '.': './cloudpack.base.json' },
      },
      'node_modules/cloudpack-base/cloudpack.base.json': {},
    });

    const resolvedPath = resolveParentConfig({
      configPath: path.join(testPath, 'apps/app/cloudpack.config.json'),
      importSpecifier: 'cloudpack-base',
    });

    expect(resolvedPath).toEqual(path.join(testPath, 'node_modules/cloudpack-base/cloudpack.base.json'));
  });

  it("throws on a package import specifier with path when it's not in exports map", async () => {
    testPath = await createLinkedMonorepo({
      'apps/app/package.json': { name: 'app', version: '1.0.0', devDependencies: { 'cloudpack-base': '*' } },
      'node_modules/cloudpack-base/package.json': {
        name: 'cloudpack-base',
        version: '1.0.0',
        exports: { '.': './index.js' },
      },
      'node_modules/cloudpack-base/cloudpack.base.json': {},
      'node_modules/cloudpack-base/index.js': '',
    });

    const configPath = path.join(testPath, 'apps/app/cloudpack.config.json');
    const importSpecifier = 'cloudpack-base/cloudpack.base.json';

    expect(() => resolveParentConfig({ configPath, importSpecifier })).toThrow(
      `Could not resolve "extends": "${importSpecifier}":\nPackage subpath './cloudpack.base.json' is not defined ` +
        `by "exports" in ${path.join(testPath, 'node_modules/cloudpack-base/package.json')} imported from ${configPath}`,
    );
  });

  it('resolves a package import specifier with path based on exports map', async () => {
    const relativeResolvedPath = 'node_modules/cloudpack-base/lib/stuff/file.json';
    testPath = await createLinkedMonorepo({
      'apps/app/package.json': { name: 'app', version: '1.0.0', devDependencies: { 'cloudpack-base': '*' } },
      'node_modules/cloudpack-base/package.json': {
        name: 'cloudpack-base',
        version: '1.0.0',
        // the cloudpack config is under some random path which is remapped in exports
        exports: { './cloudpack.base.json': './lib/stuff/file.json' },
      },
      [relativeResolvedPath]: {},
    });

    const resolvedPath = resolveParentConfig({
      configPath: path.join(testPath, 'apps/app/cloudpack.config.json'),
      importSpecifier: 'cloudpack-base/cloudpack.base.json',
    });

    expect(resolvedPath).toEqual(path.join(testPath, relativeResolvedPath));
  });

  it("throws if parent config relative path doesn't exist", async () => {
    testPath = await createLinkedMonorepo({
      'apps/app/cloudpack.config.json': {},
    });

    const configPath = path.join(testPath, 'apps/app/cloudpack.config.json');
    const importSpecifier = '../../cloudpack.base.json';

    expect(() => resolveParentConfig({ configPath, importSpecifier })).toThrow(
      `Could not resolve "extends": "${importSpecifier}":\nCannot find module '${path.join(
        testPath,
        'cloudpack.base.json',
      )}' imported from ${configPath}`,
    );
  });

  it("throws if parent config package can't be resolved", async () => {
    testPath = await createLinkedMonorepo({
      'apps/app/package.json': { name: 'app', version: '1.0.0', devDependencies: { nope: '*' } },
    });

    const configPath = path.join(testPath, 'apps/app/cloudpack.config.json');
    const importSpecifier = 'nope/cloudpack.base.json';

    expect(() => resolveParentConfig({ configPath, importSpecifier })).toThrow(
      `Could not resolve "extends": "${importSpecifier}":\nCannot find package 'nope' imported from ${configPath}`,
    );
  });

  it("throws if parent config package is resolved but file doesn't exist", async () => {
    testPath = await createLinkedMonorepo({
      'apps/app/package.json': { name: 'app', version: '1.0.0', devDependencies: { scripts: '*' } },
      'scripts/package.json': { name: 'scripts', version: '1.0.0', main: 'index.js' },
    });

    const importSpecifier = 'scripts/cloudpack.base.json';
    const configPath = path.join(testPath, 'apps/app/cloudpack.config.json');
    const resolvedPath = path.join(testPath, 'node_modules', importSpecifier);

    expect(() => resolveParentConfig({ configPath, importSpecifier })).toThrow(
      `Could not resolve "extends": "${importSpecifier}":\nCannot find module '${resolvedPath}' imported from ${configPath}`,
    );
  });
});
